import { type NetWorthAt, type NetWorthQuery, reportingKeys } from '@taler/api-client'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'

export interface UseNetWorthOptions {
  readonly ledgerId: () => string | undefined
  readonly date: () => string
  readonly displayCurrency: () => string
  readonly enabled: () => boolean
}

export interface UseNetWorth {
  readonly data: ComputedRef<NetWorthAt | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

export function useNetWorth(options: UseNetWorthOptions): UseNetWorth {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = computed<NetWorthQuery>(() => ({
    date: options.date(),
    displayCurrency: options.displayCurrency() as NetWorthQuery['displayCurrency'],
  }))

  const netWorthQuery = useQuery({
    queryKey: computed(() => reportingKeys.netWorth(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.netWorth(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })

  return {
    data: computed(() => netWorthQuery.data.value),
    isLoading: computed(() => active.value && netWorthQuery.isPending.value),
    error: computed(() => netWorthQuery.error.value),
    retrying: retrying(netWorthQuery),
    retry: () => {
      void netWorthQuery.refetch()
    },
  }
}
